import { Attribute } from "@/routes/Frontend/components/AttributeFilters/types";
import { GenericObject } from "@/types";
import { ChangeEvent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Loader from "../../../components/Loader";
import { Module, MODULE_ORDER, ModuleEnum } from "../../../constants";
import { AttributeFilters } from "../components/AttributeFilters";
import { Select } from "../ParticipationFlow/components";
import { BottomBar } from "../ParticipationFlow/components/bottom-bar";
import {
  AccessLevel,
  SelectedFilters,
  useAccessLevel,
  useFavIcon,
  useFilters,
  useRawCubeCockpitData,
  useSetCurrentLanguage,
  useUrlState,
} from "./hooks";
import { useCurrentTest } from "./hooks/useCurrentTest";
import "./index.scss";
import { Analytics } from "./modules/Analytics";
import { Reflection } from "./modules/Reflection";
import { Solution } from "./modules/Solution";

export const FuturebilityCockpit = () => {
  const { t } = useTranslation();
  const MODULES = {
    analytics: t(
      "frontend.futurebilityCockpit.moduleAnalytics",
      "Module: Analytics"
    ),
    reflection: t(
      "frontend.futurebilityCockpit.moduleReflection",
      "Module: Reflection"
    ),
    solution: t(
      "frontend.futurebilityCockpit.moduleSolution",
      "Module: Solution"
    ),
  };
  const {
    path: { testId, clientId, module },
    updatePath,
  } = useUrlState();
  const accessLevel = useAccessLevel(clientId, testId);
  const { isLoading, data: rawData } = useRawCubeCockpitData(clientId);

  const availableModules = useMemo(() => {
    const modulesData = rawData?.client?.modules || [Module.Analytics];

    return MODULE_ORDER.map(module =>
      modulesData.find(m => m === module)
    ).filter(Boolean) as ModuleEnum[];
  }, [rawData?.client?.modules]);

  const currentTest = useCurrentTest(testId, rawData);
  const [selectedModule, setSelectedModule] = useState(
    module || availableModules[0]
  );
  const {
    setCurrentSingleFilters,
    currentSingleFilters,
    selectedFiltersMultiLanguage,
  } = useFilters(currentTest, rawData?.tests, accessLevel.meta);

  const currentTestId = currentTest?._id ?? "";
  const logoUrl = rawData?.client.logoUrl?.trim() || "/logo.png";

  const selectedFilterValues = useMemo(
    () => Object.values(currentSingleFilters).flat<string[]>(),
    [currentSingleFilters]
  );

  useSetCurrentLanguage(currentTest?.languageId);
  useFavIcon(rawData?.client?.favIconUrl);

  useEffect(() => {
    if (!rawData?.client?.modules) return;

    if (module && availableModules.includes(module as ModuleEnum)) {
      setSelectedModule(module as ModuleEnum);
    } else {
      updatePath({
        module: availableModules[0],
        category: undefined,
        cubeSide: undefined,
      });
    }
  }, [module, updatePath, availableModules, rawData]);

  function handleFiltersChange(selectedAttributes: GenericObject<Attribute>) {
    const filters = Object.keys(selectedAttributes).reduce<SelectedFilters>(
      (acc, key) => {
        acc[key as any] = selectedAttributes[key].options;
        return acc;
      },
      {}
    );

    setCurrentSingleFilters(filters);
  }

  function handleModuleChange(e: ChangeEvent<HTMLSelectElement>) {
    const newModule = e.target.value as ModuleEnum;
    setSelectedModule(newModule);
    updatePath({ module: newModule, category: undefined, cubeSide: undefined });
  }

  if (accessLevel.level === AccessLevel.None) {
    return <div>Not authorized</div>;
  }

  if (isLoading || !rawData) {
    return <Loader />;
  }

  return (
    <div className="futurebility-cockpit">
      <div className="header">
        <div
          className="customer-logo"
          style={{
            backgroundImage: `url(${logoUrl})`,
          }}
        />
        <div className="module-wrapper">
          <Select
            name="module"
            value={selectedModule}
            skipEmptyOption={true}
            onChange={handleModuleChange}
            options={availableModules.map(module => ({
              label: MODULES[module],
              value: module,
            }))}
          />
        </div>
        <AttributeFilters
          test={currentTest!}
          onChange={handleFiltersChange}
          selectedValues={selectedFilterValues}
          showLabelInSelect
          disableFirstAttribute={false}
          hideSelectAll
        />
      </div>
      <section className="main">
        {selectedModule === Module.Analytics && (
          <Analytics
            selectedFilters={selectedFiltersMultiLanguage}
            currentTestId={currentTestId}
            rawData={rawData}
          />
        )}
        {selectedModule === Module.Reflection && (
          <Reflection
            selectedFilters={selectedFiltersMultiLanguage}
            currentTestId={currentTestId}
            rawData={rawData}
          />
        )}
        {selectedModule === Module.Solution && (
          <Solution
            selectedFilters={selectedFiltersMultiLanguage}
            currentTestId={currentTestId}
            rawData={rawData}
          />
        )}
      </section>
      <section className="footer">
        <BottomBar />
      </section>
    </div>
  );
};
